import { useCallback, useEffect, useRef } from 'react'

import {
  loadBusinessData,
  refreshBusinessSubscription,
} from '@disco/state-management/actions/UserActions'

import useEventListener from 'hooks/use-event-listener'
import { useDispatch } from 'hooks/use-redux'

// Quotas and plans reach the business through Chargebee's webhook, which can land after the user is back
const WEBHOOK_FOLLOW_UP_MS = 10 * 1000
// Long enough to finish a checkout, short enough that later tab switches stop costing requests
const RELOAD_WINDOW_MS = 30 * 60 * 1000

/**
 * Reloads the business and its subscription whenever the user comes back to this tab within
 * 30 minutes of opening Chargebee in a new one, so the changes made there show without a manual refresh.
 * @param onReturn - Also called on each of those returns, e.g. to request a new pricing page session
 * @returns Callback for the links that open Chargebee
 */
export default function useReloadAfterChargebee(
  onReturn?: () => void,
): () => void {
  const dispatch = useDispatch()
  // Not cleared on return: the user may glance back before confirming in Chargebee
  const openedChargebeeAt = useRef<number>(undefined)
  const isReloading = useRef(false)
  // A reload asked for during another runs after it, so an answer from before the change can't be the last word
  const queuedReload = useRef<{ withSubscription: boolean }>(undefined)
  const followUpTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(followUpTimer.current), [])

  const reload = async (withSubscription: boolean) => {
    if (isReloading.current) {
      queuedReload.current = {
        withSubscription:
          withSubscription || Boolean(queuedReload.current?.withSubscription),
      }
      return
    }

    isReloading.current = true
    // The track count doesn't change in Chargebee
    await Promise.all([
      dispatch(loadBusinessData('WITHOUT_REALTIME_TRACK_COUNT')),
      withSubscription ? dispatch(refreshBusinessSubscription()) : null,
    ])
    isReloading.current = false

    const queued = queuedReload.current
    if (queued) {
      queuedReload.current = undefined
      reload(queued.withSubscription)
    }
  }

  const onVisibilityChange = () => {
    if (
      document.hidden ||
      openedChargebeeAt.current === undefined ||
      Date.now() - openedChargebeeAt.current > RELOAD_WINDOW_MS
    )
      return

    reload(true)
    onReturn?.()
    clearTimeout(followUpTimer.current)
    // The subscription comes live from Chargebee, so only the business needs another look
    followUpTimer.current = setTimeout(() => {
      if (!document.hidden) reload(false)
    }, WEBHOOK_FOLLOW_UP_MS)
  }
  useEventListener('visibilitychange', onVisibilityChange, document)

  return useCallback(() => {
    openedChargebeeAt.current = Date.now()
  }, [])
}
